import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { LineChart } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { demandPlanningSettingsService } from '@/services/demandPlanning/demandPlanningSettings.service';

import { HelpTip, LabelWithHelp } from './HelpTip';

export function ForecastMotorSettingsCard() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canEdit = user?.role?.id === 1;

  const settings = useQuery({
    queryKey: ['demand-planning-settings'],
    queryFn: () => demandPlanningSettingsService.getSettings(),
  });

  const patch = useMutation({
    mutationFn: (forecast_respects_manual_assignments: boolean) =>
      demandPlanningSettingsService.patchSettings({ forecast_respects_manual_assignments }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['demand-planning-settings'] });
      void queryClient.invalidateQueries({ queryKey: ['forecast-run-insights'] });
    },
  });

  const manualMode = settings.data?.forecast_respects_manual_assignments ?? false;

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <LineChart className="w-5 h-5 text-stocker-primary" />
          {t('settings.forecastMotor.title')}
          <HelpTip>{t('settings.forecastMotor.help')}</HelpTip>
        </CardTitle>
        <CardDescription>{t('settings.forecastMotor.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        {settings.isError ? (
          <p className="text-sm text-red-600">{t('demandPlanning.backendError')}</p>
        ) : settings.isLoading ? (
          <p className="text-sm text-gray-500">{t('common.loading')}</p>
        ) : (
          <label className="flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 rounded border-gray-300 text-stocker-primary focus:ring-stocker-primary"
              checked={manualMode}
              disabled={!canEdit || patch.isPending}
              onChange={(e) => patch.mutate(e.target.checked)}
            />
            <span>
              <span className="block text-sm font-medium text-gray-800">
                <LabelWithHelp
                  label={t('settings.forecastMotor.manualModeLabel')}
                  help={t('settings.forecastMotor.help')}
                />
              </span>
              <span className="block text-xs text-gray-500">
                {manualMode
                  ? t('settings.forecastMotor.manualModeOnHint')
                  : t('settings.forecastMotor.manualModeOffHint')}
              </span>
            </span>
          </label>
        )}
        {!canEdit ? (
          <p className="mt-2 text-xs text-gray-500">{t('settings.forecastMotor.readOnly')}</p>
        ) : null}
        <p className="mt-3 text-xs text-gray-400">{t('settings.forecastMotor.aiEngineNote')}</p>
      </CardContent>
    </Card>
  );
}
